import { z } from "zod";
import { askSageAnswerKindSchema, askSageRoleSchema, citationTypeSchema } from "../enums.js";
import { isoDate } from "./common.js";

/**
 * Rollover thresholds. A conversation that has run this long, or been idle this
 * long, continues as a new session so a thread stays readable and its title
 * still describes it.
 */
export const ASK_SAGE_MAX_TURNS = 30;
export const ASK_SAGE_IDLE_ROLLOVER_HOURS = 24;

/** Retention floor, so a mistyped window cannot erase this week's questions. */
export const ASK_SAGE_MIN_RETENTION_DAYS = 30;
export const ASK_SAGE_MAX_RETENTION_DAYS = 3_650;

/** How long a single question may be. Long enough to paste a headline. */
export const ASK_SAGE_QUESTION_MAX_LENGTH = 1_000;

/**
 * What an answer was grounded in. `sourceId` is the dictionary entry, rule
 * version or policy version id, kept as a plain id — the cited row may later be
 * archived, and the transcript must still read correctly.
 */
export const askSageCitationSchema = z.object({
  type: citationTypeSchema,
  sourceId: z.string(),
  title: z.string(),
});
export type AskSageCitation = z.infer<typeof askSageCitationSchema>;

export const askSageMessageSchema = z.object({
  id: z.string(),
  role: askSageRoleSchema,
  /** Empty once the retention window blanked it; `purged` says why. */
  body: z.string(),
  answerKind: askSageAnswerKindSchema.nullable(),
  citations: z.array(askSageCitationSchema),
  /** True when the text was removed by retention. The turn itself remains. */
  purged: z.boolean(),
  /**
   * True when the organization's knowledge base was too large to send whole, so
   * this answer came from a relevance-scored extract. Distinguishes "nothing
   * covers this" from "Sage did not see everything".
   */
  knowledgePartial: z.boolean(),
  createdAt: isoDate,
});
export type AskSageMessage = z.infer<typeof askSageMessageSchema>;

/** List row: enough to pick a conversation without loading its turns. */
export const askSageSessionSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  userEmail: z.string(),
  channelName: z.string().nullable(),
  categoryName: z.string().nullable(),
  messageCount: z.number().int().nonnegative(),
  /** Set when this conversation rolled over from an earlier one. */
  continuedFromId: z.string().nullable(),
  purged: z.boolean(),
  lastMessageAt: isoDate,
  createdAt: isoDate,
});
export type AskSageSessionSummary = z.infer<typeof askSageSessionSummarySchema>;

export const askSageSessionSchema = askSageSessionSummarySchema.extend({
  channelId: z.string().nullable(),
  categoryId: z.string().nullable(),
  messages: z.array(askSageMessageSchema),
});
export type AskSageSession = z.infer<typeof askSageSessionSchema>;

/**
 * Ask a question. Omitting `sessionId` starts a conversation; the optional
 * channel and category narrow which standards Sage retrieves and are fixed for
 * the life of the session, so every answer in a transcript was grounded in the
 * same scope.
 */
export const askSageQuestionSchema = z.object({
  sessionId: z.string().min(1).optional(),
  question: z.string().trim().min(2).max(ASK_SAGE_QUESTION_MAX_LENGTH),
  channelId: z.string().min(1).nullable().optional(),
  categoryId: z.string().min(1).nullable().optional(),
});
export type AskSageQuestionInput = z.infer<typeof askSageQuestionSchema>;

export const askSageAnswerSchema = z.object({
  session: askSageSessionSchema,
  answer: askSageMessageSchema,
});
export type AskSageAnswer = z.infer<typeof askSageAnswerSchema>;

export const listAskSageSessionsQuerySchema = z.object({
  /** Admin log only: restrict to one member's conversations. */
  userEmail: z.string().trim().min(1).optional(),
  organizationId: z.string().min(1).optional(),
  search: z.string().trim().min(1).max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListAskSageSessionsQuery = z.infer<typeof listAskSageSessionsQuerySchema>;

export const askSageSessionListSchema = z.object({
  sessions: z.array(askSageSessionSummarySchema),
});
export type AskSageSessionList = z.infer<typeof askSageSessionListSchema>;

/**
 * Usage the administrators asked for: how much the channel is used, by whom,
 * and how often Sage could not answer from the knowledge base — the last being
 * the number that tells an admin their dictionary or standards have a gap.
 */
export const askSageStatsSchema = z.object({
  sessionCount: z.number().int().nonnegative(),
  questionCount: z.number().int().nonnegative(),
  askerCount: z.number().int().nonnegative(),
  answersByKind: z.record(askSageAnswerKindSchema, z.number().int().nonnegative()),
  /** Most active askers, highest first. */
  topAskers: z.array(
    z.object({ userEmail: z.string(), questionCount: z.number().int().nonnegative() }),
  ),
  /** Rules and dictionary entries cited most often, highest first. */
  topCitations: z.array(
    z.object({
      type: citationTypeSchema,
      sourceId: z.string(),
      title: z.string(),
      citedCount: z.number().int().nonnegative(),
    }),
  ),
  lastAskedAt: isoDate.nullable(),
});
export type AskSageStats = z.infer<typeof askSageStatsSchema>;
